import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { ArrowUpDown, Settings, RefreshCw } from 'lucide-react';
import { TokenSelector } from './TokenSelector';
import { SwapSettings } from './SwapSettings';
import { SwapPreview } from './SwapPreview';
import { useWallet } from '../wallet/WalletProvider';

interface SwapState {
  tokenIn: string;
  tokenOut: string;
  amountIn: string;
  amountOut: string;
  slippage: number;
  deadline: number;
}

export function SwapInterface() {
  const { isConnected } = useWallet();
  const [swapState, setSwapState] = useState<SwapState>({
    tokenIn: 'ETH',
    tokenOut: 'USDC',
    amountIn: '',
    amountOut: '',
    slippage: 0.5,
    deadline: 20,
  });
  const [showSettings, setShowSettings] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSwapTokens = () => {
    setSwapState(prev => ({
      ...prev,
      tokenIn: prev.tokenOut,
      tokenOut: prev.tokenIn,
      amountIn: prev.amountOut,
      amountOut: prev.amountIn,
    }));
  };

  const handleAmountChange = (value: string, isInput: boolean) => {
    if (isInput) {
      setSwapState(prev => ({ ...prev, amountIn: value }));
      // In real app, would calculate amountOut based on current prices
      const estimatedOut = (parseFloat(value) * 2845.67).toString();
      setSwapState(prev => ({ ...prev, amountOut: estimatedOut }));
    } else {
      setSwapState(prev => ({ ...prev, amountOut: value }));
    }
  };

  const handleSwap = async () => {
    if (!isConnected) return;
    
    setIsLoading(true);
    try {
      // In real app, would execute swap transaction
      console.log('Executing swap:', swapState);
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate transaction
    } catch (error) {
      console.error('Swap failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isConnected) {
    return (
      <Card className="max-w-md mx-auto">
        <CardHeader>
          <CardTitle>Token Swap</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-slate-600 dark:text-slate-400">
            Connect your wallet to start swapping tokens.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Swap Tokens</h1>
        <p className="text-slate-600 dark:text-slate-400 mt-2">
          Trade tokens instantly at the best prices across multiple DEXs
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Swap</CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.location.reload()}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSettings(!showSettings)}
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {showSettings && (
            <SwapSettings
              slippage={swapState.slippage}
              deadline={swapState.deadline}
              onSlippageChange={(slippage) => setSwapState(prev => ({ ...prev, slippage }))}
              onDeadlineChange={(deadline) => setSwapState(prev => ({ ...prev, deadline }))}
            />
          )}

          {/* From Token */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">From</label>
            <TokenSelector
              selectedToken={swapState.tokenIn}
              amount={swapState.amountIn}
              onTokenSelect={(token) => setSwapState(prev => ({ ...prev, tokenIn: token }))}
              onAmountChange={(value) => handleAmountChange(value, true)}
              balance="5.2847"
              isInput={true}
            />
          </div>

          {/* Swap Button */}
          <div className="flex justify-center">
            <Button
              variant="ghost"
              size="sm"
              className="w-10 h-10 rounded-full border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
              onClick={handleSwapTokens}
            >
              <ArrowUpDown className="h-4 w-4" />
            </Button>
          </div>

          {/* To Token */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">To</label>
            <TokenSelector
              selectedToken={swapState.tokenOut}
              amount={swapState.amountOut}
              onTokenSelect={(token) => setSwapState(prev => ({ ...prev, tokenOut: token }))}
              onAmountChange={(value) => handleAmountChange(value, false)}
              balance="1,234.56"
              isInput={false}
            />
          </div>

          {swapState.amountIn && swapState.amountOut && (
            <SwapPreview
              tokenIn={swapState.tokenIn}
              tokenOut={swapState.tokenOut}
              amountIn={swapState.amountIn}
              amountOut={swapState.amountOut}
              slippage={swapState.slippage}
              gasEstimate="0.008"
            />
          )}

          <Button
            className="w-full h-12"
            onClick={handleSwap}
            disabled={!swapState.amountIn || isLoading}
          >
            {isLoading ? 'Swapping...' : 'Swap Tokens'}
          </Button>

          <div className="text-xs text-center text-slate-500 dark:text-slate-400">
            Powered by 1inch, Uniswap, and other leading DEX aggregators
          </div>
        </CardContent>
      </Card>
    </div>
  );
}